import { TranslateService } from "@ngx-translate/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import LoggerService from "@bfx/logger";
import { ZERO } from "shared/base-components/number-spinner.component";
import {
  BmarkBaseRiskDisplayModel,
  BmarkRiskPricingErrorDisplayModel,
} from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
import { InstrumentColumnData } from "../../../../../shared/cubiq-services/model/benchmarks/BmarkSpotRisk.constants";
import { ICellRendererParams } from "ag-grid-community/main";
const logger = LoggerService.for(__dirname);
const INTENT_SELL: string = "S";
const INTENT_BUY: string = "B";
const ONE_MILLION: CubiqTS.BigNumber = new CubiqTS.BigNumber(1000000);

const BMARK_SPOTRISK_POPOUT_TITLE = "bmark.spotrisk.popout.title";
const BMARK_SPOTRISK_POPOUT_TITLE_DEFAULT = "Pricing Error/Expired";

/**
 * Data to be shown in the Popover overlay
 */
type PopoutInfo = {
  title: string;
  rows: Array<string>;
  total: string;
};

/**
 * Common renderer functionality for instrument in Risk blotters
 */
export abstract class BmarkRiskInstrumentRendererComponent {
  public instrument: string;
  public warningOverlayInfo: string;
  public warningOverlayEnabled: boolean = false;
  public params: any;
  public cell: any;

  /** When false, no valueDates button is visible */
  public hasFwdOrders: boolean = false;

  public popoutInfo: PopoutInfo = {
    title: BMARK_SPOTRISK_POPOUT_TITLE_DEFAULT,
    rows: new Array<string>(),
    total: "",
  };

  /**
   * Popout infor where error defined on the BmarkRiskDisplayModel
   */
  protected errorInfo: PopoutInfo;

  /**
   * BmarkRiskInstrumentRendererComponent is immutable' after agInit() due to refresh() returning false
   *
   * Change from ag-grid will destroy component and create a new one
   *
   * A ChangeDetectorRef push (markForCheck) must never be called
   */
  constructor(protected translator: TranslateService) {
    if (null != translator) {
      this.popoutInfo.title = this.translator.instant(BMARK_SPOTRISK_POPOUT_TITLE);
    }
  }

  public agInit(params: ICellRendererParams): void {
    this.params = params;
    this.cell = { row: params.value, col: params.colDef.headerName };
    this.processParams(params);
  }

  public refresh(): boolean {
    /**
     * by returning false.
     *
     * ag-grid will destroy and recreate this cell.
     *
     */
    return false;
  }

  protected processParams(params: ICellRendererParams): void {
    if (params != null && params.value != null && params.value instanceof InstrumentColumnData) {
      const pairModel: BmarkBaseRiskDisplayModel = params.data;

      let data: InstrumentColumnData = params.value;
      this.warningOverlayEnabled =
        data.pricingError instanceof BmarkRiskPricingErrorDisplayModel &&
        pairModel.pricingError.orderCount > 0;
      this.instrument = data.instrument;
      this.hasFwdOrders = pairModel.hasFwdOrders();

      if (this.warningOverlayEnabled) {
        this.errorInfo = this.createErrorInfo(params.data);
      }
    }
  }

  private createErrorInfo(pairModel: BmarkBaseRiskDisplayModel): PopoutInfo {
    const info: PopoutInfo = {
      title: BMARK_SPOTRISK_POPOUT_TITLE_DEFAULT,
      rows: new Array<string>(),
      total: "",
    };
    for (const tgData of pairModel.pricingError.tgData) {
      let row =
        tgData.tgName +
        " - " +
        tgData.orderCount +
        " orders - " +
        this.intent(tgData.totalAmount) +
        " " +
        this.millions(tgData.totalAmount) +
        "M";
      info.rows.push(row);
    }

    info.total =
      "Total - " +
      pairModel.pricingError.orderCount +
      " orders - " +
      this.intent(pairModel.pricingError.totalAmount) +
      " " +
      this.millions(pairModel.pricingError.totalAmount) +
      "M";
    return info;
  }

  millions(totalAmount: CubiqTS.BigNumber): string {
    return totalAmount.dividedBy(ONE_MILLION).toFixed(2).toString();
  }

  intent(amount: CubiqTS.BigNumber): string {
    return amount.isLessThanOrEqualTo(ZERO) ? INTENT_SELL : INTENT_BUY;
  }
}
